import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  FileText,
  ChevronRight,
  Clock,
  Search,
  LayoutGrid,
  InfoIcon,
} from "lucide-react";
import { useGetMyProbationerApplicationsQuery } from "../api/probationerApplicationApi";
import { statusColors, statusLabel } from "../utils/applicationStatus";
import { useAuth } from "../../../context/AuthContexts";

// --- Helpers ---
function safeMsg(err: any) {
  return (
    err?.data?.message || err?.error || err?.message || "Something went wrong"
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

// --- Sub-components ---
function StatusBadge({ status }: { status: string }) {
  // One palette and one set of words for the ladder, shared with the admin and
  // chapter screens — an applicant and the secretariat must not be reading
  // different names for the same rung.
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusColors(status).pill}`}
      title={status}
    >
      {statusLabel(status)}
    </span>
  );
}

function StatCard({ title, value }: { title: string; value: string | number }) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="text-sm text-gray-600">{title}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

// --- Main Component ---
export default function ApplicationListPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetMyProbationerApplicationsQuery();

  // 1. Filtering Logic
  const filteredApps = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return data.filter((app: any) => {
      const nameBlob =
        `${app.title} ${app.surname} ${app.other_names}`.toLowerCase();
      const matchesSearch = !nq || nameBlob.includes(nq);
      const matchesStatus =
        statusFilter === "all" || app.status?.toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, q, statusFilter]);

  // 2. Stats Calculation
  const stats = useMemo(() => {
    return {
      total: data.length,
      pending: data.filter((a: any) => a.status?.toLowerCase() === "pending")
        .length,
      approved: data.filter((a: any) => a.status?.toLowerCase() === "approved")
        .length,
    };
  }, [data]);

  // 3. Most recent application — sorted by created_at desc
  const latestApp = useMemo(() => {
    if (!data.length) return null;
    return [...data].sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0] as any;
  }, [data]);

  if (user?.member_id) {
    return <Navigate to="/dashboard" replace />;
  }

  // Only allow a new application when there are no apps yet, or the most
  // recent one was rejected (the user needs to reapply).
  const canApplyNew =
    !isLoading && latestApp?.status?.toLowerCase() === "rejected";

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-6">
      {/* Header Card */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-50 border">
              <LayoutGrid className="w-5 h-5 text-gray-700" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">
                Probationer Applications
              </h1>
              <p className="text-sm text-gray-600">
                Track and manage your registration progress
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            {canApplyNew ? (
              <Link
                to="/probationer-applications/new"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-all"
              >
                <Plus className="w-4 h-4" />
                New Application
              </Link>
            ) : latestApp ? (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-500 text-sm font-medium cursor-not-allowed select-none"
                title={`Your current application is at ${statusLabel(latestApp.status)}. A new application can only be started after a rejection.`}
              >
                <InfoIcon className="w-4 h-4 shrink-0" />
                Application {statusLabel(latestApp.status).toLowerCase()}
              </div>
            ) : null}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-7">
            <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1 ml-1">
              Search Applications
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1 ml-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Draft</option>
              <option value="acknowledged">Awaiting endorsements</option>
              <option value="under_review">Secretariat review</option>
              <option value="with_committee">With committee</option>
              <option value="returned">Returned</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <button
              onClick={() => {
                setQ("");
                setStatusFilter("all");
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 text-gray-500"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard title="Total Submissions" value={stats.total} />
        <StatCard title="In Progress" value={stats.pending} />
        <StatCard title="Approved" value={stats.approved} />
      </div>

      {/* Main Content Area */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-20 text-center">
            <div className="w-6 h-6 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading records...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center text-red-500 text-sm px-4">
            {safeMsg(error)}
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">No matching applications found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredApps.map((app: any) => (
              <div
                key={app.id}
                className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 shrink-0 border">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-gray-800 text-sm uppercase">
                        {app.title} {app.surname} {app.other_names}
                      </h3>
                      <StatusBadge status={app.status} />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 font-medium">
                      <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                        Step {app.completion_step}
                      </span>
                      <span className="flex items-center gap-1 uppercase tracking-wider">
                        <Clock className="w-3 h-3" /> {fmtDate(app.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                  <div className="hidden lg:block text-right mr-4">
                    <div className="text-[10px] text-gray-300 font-bold uppercase">
                      System ID
                    </div>
                    <div className="text-[10px] font-mono text-gray-400">
                      {app.id?.slice(0, 8)}
                    </div>
                  </div>

                  <Link
                    to={
                      app.status?.toLowerCase() === "pending"
                        ? `/probationer-applications/new?step=${
                            (app.completion_step || 0) + 1
                          }`
                        : `/probationer-applications/${app.id}`
                    }
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                      app.status?.toLowerCase() === "pending"
                        ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {app.status?.toLowerCase() === "pending"
                      ? "Resume"
                      : "View Details"}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
